import React, { useState, useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { FiX, FiSave } from '../../utils/icons';
import { Domain } from '../../pages/ControlDomains';

interface DomainFormProps {
  domain?: Domain | null;
  parentId?: number | null;
  empresaId: number;
  frameworkId: number;
  onSave: (data: Partial<Domain>) => void;
  onCancel: () => void;
  suggestedIndex?: string;
}

const DomainForm: React.FC<DomainFormProps> = ({
  domain, parentId, empresaId, frameworkId, onSave, onCancel, suggestedIndex,
}) => {
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [nivel, setNivel] = useState('');
  const [parentPrefix, setParentPrefix] = useState('');

  const isSubdomain = !!(parentId || domain?.parent_id);

  useEffect(() => {
    if (domain) {
      setNome(domain.nome);
      setDescricao(domain.descricao || '');
      if (domain.parent_id) {
        const parts = domain.indice.split('.');
        setNivel(parts[parts.length - 1]);
        setParentPrefix(parts.slice(0, -1).join('.'));
      } else {
        setNivel(domain.indice);
        setParentPrefix('');
      }
    } else {
      setNome('');
      setDescricao('');
      if (parentId && suggestedIndex) {
        const parts = suggestedIndex.split('.');
        setNivel(parts[parts.length - 1]);
        setParentPrefix(parts.slice(0, -1).join('.'));
      } else {
        setNivel(suggestedIndex || '');
        setParentPrefix('');
      }
    }
  }, [domain, suggestedIndex, parentId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const fullIndice = parentPrefix ? `${parentPrefix}.${nivel.trim()}` : nivel.trim();
    const data: Partial<Domain> = {
      nome: nome.trim(),
      descricao: descricao.trim(),
      indice: fullIndice,
      parent_id: domain ? domain.parent_id : (parentId ?? null),
      empresa_id: empresaId,
      framework_id: frameworkId,
    };
    if (domain) data.id = domain.id;
    onSave(data);
  };

  const formTitle = domain ? 'Editar Domínio' : (parentId ? 'Novo Subdomínio' : 'Novo Domínio');

  return (
    <Modal show onHide={onCancel} centered>
      <Modal.Header closeButton>
        <Modal.Title>{formTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="domain-nome">
            <Form.Label>{isSubdomain ? 'Nome do Subdomínio' : 'Nome'} *</Form.Label>
            <Form.Control
              type="text"
              value={nome}
              onChange={e => setNome(e.target.value)}
              placeholder="Ex: Proteção de Dados Pessoais"
              required
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="domain-nivel">
            <Form.Label>{isSubdomain ? 'Nível' : 'Índice'} *</Form.Label>
            <Form.Control
              type="text"
              value={nivel}
              onChange={e => setNivel(e.target.value)}
              placeholder={isSubdomain ? 'Ex: 1' : 'Ex: 4'}
              required
            />
            {isSubdomain && parentPrefix && (
              <Form.Text className="text-muted">
                Índice completo: <strong>{parentPrefix}.{nivel || '?'}</strong>
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3" controlId="domain-descricao">
            <Form.Label>Descrição</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={descricao}
              onChange={e => setDescricao(e.target.value)}
              placeholder="Descreva o escopo e objetivo deste domínio (opcional)"
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel}><FiX /> Cancelar</Button>
        <Button variant="primary" onClick={handleSubmit}><FiSave /> Salvar</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DomainForm;
